import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Mic, MicOff, X, Loader2, AudioWaveform, Sparkles, MessageCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LexiaRealtimeSession } from "@/lib/realtime";
import { useAuth } from "@/contexts/AuthContext";
import { useDashboard } from "@/contexts/DashboardContext";
import { useLexiaVoiceRequests } from "@/contexts/LexiaVoiceContext";
import { announceDailyVoiceDone } from "@/components/DailyVoiceCard";
import LexiaAvatar from "@/components/LexiaAvatar";

/* -------------------------------------------------------------------------- */
/*  Navigation table used by the `navigate` tool.                             */
/*  Keep in sync with realtime_router._TOOLS.parameters.properties.page.enum. */
/* -------------------------------------------------------------------------- */
const NAV_MAP = {
    dashboard: "/dashboard",
    lesson: "/lesson",
    reviews: "/reviews",
    conversation: "/conversation",
    translate: "/translate",
    podcasts: "/podcasts",
    parcours: "/parcours",
    progress: "/progress",
    settings: "/settings/profile",
    medical: "/parcours/medical",
    intensive: "/intensive",
};

const IDLE_TIMEOUT_MS = 120_000; // auto-close after 2 min of assistant/user inactivity

/* -------------------------------------------------------------------------- */

export default function LexiaVoiceWidget() {
    const { user } = useAuth();
    const { data: dashboard, refresh: refreshDashboard } = useDashboard();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [state, setState] = useState("idle"); // idle | connecting | listening | speaking | mic_denied | error | closed
    const [error, setError] = useState("");
    const [muted, setMuted] = useState(false);
    const [transcript, setTranscript] = useState([]); // [{role:'user'|'assistant', text, done}]
    const [mode, setMode] = useState("general"); // 'general' | 'daily_voice'
    const [targetWords, setTargetWords] = useState([]);
    const sessionRef = useRef(null);
    const idleTimerRef = useRef(null);
    const activeAssistantIdxRef = useRef(-1);
    const sessionStartedAtRef = useRef(0);
    const practicedCountRef = useRef(0);
    const modeRef = useRef("general");

    // Auto-open request from another page (e.g. dashboard's daily-voice card).
    useLexiaVoiceRequests((requestedMode, hint) => {
        setMode(requestedMode || "general");
        modeRef.current = requestedMode || "general";
        // Seed the target words from the caller's hint so the panel banner
        // renders the correct list instantly (before /realtime/token resolves).
        if (Array.isArray(hint?.target_words)) setTargetWords(hint.target_words);
        setOpen(true);
        // Kick off immediately so the user doesn't have to click twice.
        setTimeout(() => {
            if (!sessionRef.current) startSessionInMode(requestedMode || "general");
        }, 60);
    });

    /* ------------------------ session lifecycle ------------------------ */

    function resetIdleTimer() {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            toast.info("Session vocale fermée automatiquement (2 min sans activité).");
            closeSession();
        }, IDLE_TIMEOUT_MS);
    }

    function closeSession() {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
        const wasDailyVoice = modeRef.current === "daily_voice";
        const wasActive = !!sessionRef.current;
        const startedAt = sessionStartedAtRef.current;
        const practicedCount = practicedCountRef.current;
        try { sessionRef.current?.close(); } catch { /* noop */ }
        sessionRef.current = null;
        setState("idle");
        setMuted(false);
        activeAssistantIdxRef.current = -1;
        sessionStartedAtRef.current = 0;
        practicedCountRef.current = 0;
        modeRef.current = "general";
        // Finalize the daily voice session if applicable.
        if (wasActive && wasDailyVoice && startedAt > 0) {
            const durationS = Math.min(1800, Math.round((Date.now() - startedAt) / 1000));
            api.post("/daily-voice/complete", { duration_s: durationS })
                .then(() => {
                    try { announceDailyVoiceDone(); } catch { /* noop */ }
                    refreshDashboard?.();
                    if (practicedCount > 0) {
                        toast.success(`Session vocale terminée — ${practicedCount} mot${practicedCount > 1 ? "s" : ""} pratiqué${practicedCount > 1 ? "s" : ""}. À demain !`);
                    } else {
                        toast.info("Session vocale terminée. À demain pour la suite.");
                    }
                })
                .catch(() => { /* silent — non blocking */ });
        }
    }

    async function startSession() {
        return startSessionInMode(modeRef.current || "general");
    }

    async function startSessionInMode(nextMode) {
        if (sessionRef.current) return;
        setError("");
        setTranscript([]);
        setState("connecting");
        setMode(nextMode);
        modeRef.current = nextMode;
        practicedCountRef.current = 0;
        let payload;
        try {
            const { data } = await api.post("/realtime/token", { mode: nextMode });
            payload = data;
            // Only overwrite the pre-seeded hint list if the backend returned
            // one — this preserves the banner during connecting for daily_voice.
            if (Array.isArray(data?.target_words) && data.target_words.length > 0) {
                setTargetWords(data.target_words);
            } else if (nextMode !== "daily_voice") {
                setTargetWords([]);
            }
        } catch (err) {
            setState("error");
            setError(formatApiError(err));
            return;
        }
        const session = new LexiaRealtimeSession({
            onState: (s) => { setState(s); resetIdleTimer(); },
            onError: (e) => { setState("error"); setError(e?.message || "Erreur inconnue."); },
            onTranscriptUser: (text) => {
                setTranscript((prev) => [...prev, { role: "user", text, done: true }]);
                resetIdleTimer();
            },
            onTranscriptAssistant: (text, done) => {
                setTranscript((prev) => {
                    const idx = activeAssistantIdxRef.current;
                    if (idx >= 0 && !prev[idx]?.done) {
                        const next = prev.slice();
                        next[idx] = { role: "assistant", text, done };
                        return next;
                    }
                    activeAssistantIdxRef.current = prev.length;
                    return [...prev, { role: "assistant", text, done }];
                });
                if (done) activeAssistantIdxRef.current = -1;
                resetIdleTimer();
            },
            onEvent: () => resetIdleTimer(),
            onToolCall: async (name, args) => handleToolCall(name, args),
        });
        sessionRef.current = session;
        try {
            await session.connect({ ephemeralToken: payload.ephemeral_token, model: payload.model });
            await session.waitForDataChannel(6000);
            sessionStartedAtRef.current = Date.now();
            resetIdleTimer();
        } catch (err) {
            setState("error");
            setError(err?.message || "Impossible d'ouvrir la session vocale.");
            closeSession();
        }
    }

    /* --------------------------- tool calls ---------------------------- */

    async function handleToolCall(name, args) {
        try {
            if (name === "navigate") {
                const path = NAV_MAP[args.page];
                if (!path) return { ok: false, error: `Page inconnue : ${args.page}` };
                navigate(path);
                return { ok: true, page: args.page, path };
            }
            if (name === "set_slow_mode") {
                const nextSlow = !!args.enabled;
                const current = dashboard?.profile;
                if (!current) return { ok: false, error: "Profil indisponible." };
                await api.post("/profile/save", { ...current, slow_pace: nextSlow });
                refreshDashboard?.();
                return { ok: true, slow_pace: nextSlow };
            }
            if (name === "translate") {
                const targetLang = dashboard?.profile?.target_language || "en-US";
                const direction = args.direction || "to_target";
                const { data } = await api.post("/translate", {
                    text: args.text || "",
                    source_language: direction === "to_target" ? "français" : targetLang,
                    target_language: direction === "to_target" ? targetLang : "français",
                });
                return { ok: true, translation: data?.translation || data?.target_text || "" };
            }
            if (name === "get_progress_summary") {
                const [{ data: d1 }, { data: d2 }] = await Promise.all([
                    api.get("/dashboard"),
                    api.get("/dashboard/progress"),
                ]);
                return {
                    ok: true,
                    streak: d2?.streak_days ?? d1?.stats?.streak_days ?? 0,
                    mastered: d2?.mastered ?? d1?.stats?.mastered ?? 0,
                    active: d2?.active ?? d1?.stats?.active ?? 0,
                    fragile: d2?.fragile ?? d1?.stats?.fragile ?? 0,
                    next_review_at: d2?.next_review_at ?? null,
                };
            }
            if (name === "start_challenge") {
                navigate("/lesson");
                return { ok: true, hint: "Leçon du jour ouverte." };
            }
            if (name === "record_word_practice") {
                const w = (args.word || "").toString().trim();
                const note = Math.max(0, Math.min(5, Number(args.note) || 0));
                if (!w) return { ok: false, error: "Mot vide." };
                try {
                    const { data } = await api.post("/daily-voice/practice-word", { word: w, note });
                    if (data?.ok) practicedCountRef.current += 1;
                    return data;
                } catch (err) {
                    return { ok: false, error: err?.message || "Erreur SRS." };
                }
            }
        } catch (err) {
            return { ok: false, error: err?.message || "Erreur d'outil." };
        }
        return { ok: false, error: `Outil non implémenté : ${name}` };
    }

    /* ---------------------------- UI ---------------------------------- */

    useEffect(() => {
        // Close the session when the widget is unmounted (route change to /login etc.).
        return () => closeSession();
    }, []);

    useEffect(() => {
        // If the user closes the panel, tear down the session — never leave
        // a WebRTC connection open in the background.
        if (!open && sessionRef.current) closeSession();
    }, [open]);

    // Don't render for anonymous users (hooks above must still be called).
    if (!user) return null;

    function toggleMute() {
        const next = !muted;
        setMuted(next);
        sessionRef.current?.setMicMuted(next);
    }

    const stateLabels = {
        idle: "Toucher la bulle pour parler à Lexia",
        connecting: "Connexion…",
        listening: "Je vous écoute — parlez librement",
        speaking: "Lexia parle…",
        mic_denied: "Micro refusé",
        error: "Erreur",
        closed: "Session fermée",
    };

    const stateColor = {
        connecting: "bg-inkSoft",
        listening: "bg-emerald",
        speaking: "bg-terracotta",
        mic_denied: "bg-terracotta",
        error: "bg-terracotta",
    }[state];

    return (
        <>
            {/* Floating bubble */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="lexia-voice-bubble"
                    className="fixed bottom-6 right-6 z-[60] group inline-flex items-center justify-center transition-transform active:scale-95 hover:-translate-y-0.5"
                    aria-label="Ouvrir Lexia — coach vocale"
                >
                    <span className="relative inline-block">
                        <LexiaAvatar size="md" state="static" />
                        {/* Small mic pip on the bottom-right */}
                        <span
                            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-terracotta text-white shadow-md shadow-black/25 inline-flex items-center justify-center border-2 border-white"
                            aria-hidden="true"
                        >
                            <Mic className="w-3 h-3" strokeWidth={2} />
                        </span>
                    </span>
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    data-testid="lexia-voice-panel"
                    className="fixed bottom-6 right-6 z-[60] w-[min(360px,calc(100vw-32px))] max-h-[calc(100vh-48px)] bg-white border border-lexiBorder rounded-2xl shadow-2xl shadow-black/15 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200"
                >
                    <header className="flex items-center justify-between gap-2 p-3 border-b border-lexiBorder bg-paperAlt">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <LexiaAvatar size="sm" state={state} />
                            <div className="min-w-0">
                                <p className="font-serif text-sm truncate inline-flex items-center gap-1.5">
                                    Lexia
                                    <span className="text-inkSoft text-[11px]">— coach vocale</span>
                                </p>
                                <p className="text-[10px] text-inkSoft truncate inline-flex items-center gap-1" data-testid="lexia-state-label">
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${stateColor || "bg-inkSoft/40"} ${state === "listening" || state === "speaking" ? "animate-pulse" : ""}`} data-testid="lexia-state-dot" />
                                    {state === "error" && error ? error : stateLabels[state]}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="lexia-voice-close"
                            className="text-inkSoft hover:text-ink rounded-full p-1 hover:bg-lexiBorder/50"
                            aria-label="Fermer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </header>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-paper text-sm min-h-[220px]">
                        {mode === "daily_voice" && (
                            <div className="rounded-xl bg-terracotta/8 border border-terracotta/30 p-3 text-[11px] text-ink" data-testid="daily-voice-context">
                                <p className="tracking-[0.22em] uppercase text-terracotta font-semibold">Question du jour</p>
                                {targetWords.length > 0 ? (
                                    <p className="mt-1 leading-snug">
                                        Objectif : produire à l&apos;oral{" "}
                                        <span className="font-medium">
                                            {targetWords.slice(0, 5).map((w) => w.word).join(", ")}
                                        </span>. Lexia t&apos;amène là naturellement, sans stress.
                                    </p>
                                ) : (
                                    <p className="mt-1 leading-snug">
                                        Aucun mot fragile aujourd&apos;hui — mini-conversation libre pour t&apos;échauffer.
                                    </p>
                                )}
                            </div>
                        )}
                        {state === "idle" && (
                            <div className="text-center text-inkSoft py-8">
                                <MessageCircle className="w-6 h-6 mx-auto mb-2 text-terracotta" strokeWidth={1.5} />
                                <p className="text-xs leading-relaxed">
                                    Parle-moi comme à une prof :<br />
                                    « Comment on dit &laquo;&nbsp;je voudrais un rendez-vous&nbsp;&raquo; en anglais&nbsp;? »<br />
                                    « Ouvre mes révisions. » — « Corrige ma phrase. »
                                </p>
                            </div>
                        )}
                        {state === "connecting" && (
                            <div className="flex items-center justify-center gap-2 text-inkSoft py-10">
                                <Loader2 className="w-5 h-5 animate-spin" /> Connexion vocale…
                            </div>
                        )}
                        {state === "mic_denied" && (
                            <div className="text-center text-terracotta py-8" data-testid="lexia-mic-denied">
                                <MicOff className="w-6 h-6 mx-auto mb-2" strokeWidth={1.5} />
                                <p className="text-xs">
                                    Accès micro refusé.<br />
                                    Autorisez le micro dans les réglages de votre navigateur, puis réessayez.
                                </p>
                            </div>
                        )}
                        {state === "error" && (
                            <div className="text-center text-terracotta py-8" data-testid="lexia-error">
                                <p className="text-xs">{error || "Une erreur est survenue."}</p>
                            </div>
                        )}
                        {transcript.length > 0 && (
                            <ul className="space-y-2" data-testid="lexia-transcript">
                                {transcript.map((t, i) => (
                                    <li key={i} className={t.role === "user" ? "text-right" : "text-left"}>
                                        <span
                                            className={`inline-block max-w-[85%] rounded-2xl px-3 py-1.5 text-[13px] leading-snug ${t.role === "user"
                                                ? "bg-emerald/10 border border-emerald/30 text-ink"
                                                : "bg-white border border-lexiBorder text-ink"}`}
                                        >
                                            {t.text || (t.done ? "" : <span className="text-inkSoft italic">…</span>)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Controls */}
                    <div className="p-3 border-t border-lexiBorder flex items-center gap-2 bg-paperAlt">
                        {state === "idle" || state === "closed" || state === "error" || state === "mic_denied" ? (
                            <button
                                onClick={startSession}
                                data-testid="lexia-start"
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full py-2 text-sm font-medium"
                            >
                                <Mic className="w-4 h-4" /> Démarrer la conversation
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={toggleMute}
                                    data-testid="lexia-toggle-mic"
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm ${muted
                                        ? "bg-terracotta text-white"
                                        : "bg-white border border-lexiBorder text-ink hover:bg-paperAlt"}`}
                                >
                                    {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                                    {muted ? "Micro coupé" : "Micro"}
                                </button>
                                <div className="flex-1 flex items-center justify-center gap-1 text-inkSoft">
                                    <AudioWaveform className={`w-4 h-4 ${state === "listening" ? "text-emerald" : "text-terracotta"}`} strokeWidth={1.5} />
                                    <span className="text-[11px]">{stateLabels[state]}</span>
                                </div>
                                <button
                                    onClick={closeSession}
                                    data-testid="lexia-stop"
                                    className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm bg-ink text-white hover:bg-emerald"
                                >
                                    Terminer
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
